import { useEffect, useMemo, useState } from 'react'
import { replaceExchangeShortname } from '../../utils/exchange-utils'
import { type StrategyDataGridRow } from '../../utils/view-specific/strategy/strategy-utils'
import { type IStrategyComponentRecord } from '../../models/strategy/strategy-component-types'

export interface MainSummaryData {
  bestAggBid: string
  bestAggAsk: string
  bestPassiveBid: string
  bestPassiveAsk: string
  bestAggBidValue: number
  bestAggAskValue: number
  bestAggMidValue: number
  bestPassiveBidValue: number
  bestPassiveAskValue: number
  bestPassiveMidValue: number
  refBid: number
  refAsk: number
  voidBid: number
  voidAsk: number
  volatility: string
  tradedVolume: string
  totalExposure: string
  realizedExposure: string
  totalPredFunding: string
}

export function useMainSummaryDataGridWorker (componentMap: IStrategyComponentRecord): { dataGridRows: StrategyDataGridRow[], mainSummaryData: MainSummaryData } {
  const [dataGridRows, setDataGridRows] = useState<StrategyDataGridRow[]>([])
  const [mainSummaryData, setMainSummaryData] = useState<MainSummaryData>({
    bestAggBid: '',
    bestAggAsk: '',
    bestPassiveBid: '',
    bestPassiveAsk: '',
    bestAggBidValue: NaN,
    bestAggAskValue: NaN,
    bestAggMidValue: NaN,
    bestPassiveBidValue: NaN,
    bestPassiveAskValue: NaN,
    bestPassiveMidValue: NaN,
    refBid: NaN,
    refAsk: NaN,
    voidBid: NaN,
    voidAsk: NaN,
    volatility: '',
    tradedVolume: '',
    totalExposure: '',
    realizedExposure: '',
    totalPredFunding: '',
  })

  const mainSummaryDataGridWorker: Worker = useMemo(
    () => new Worker(new URL('../../workers/strategy/main-summary-data-grid-worker.ts', import.meta.url)),
    [],
  )

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (window.Worker) {
      // Prepare flattened data for data grid display
      mainSummaryDataGridWorker.postMessage(componentMap)

      // Extract specific data for aggregated display
      const mainSummaryData: MainSummaryData = {
        bestAggBid: '',
        bestAggAsk: '',
        bestPassiveBid: '',
        bestPassiveAsk: '',
        bestAggBidValue: NaN,
        bestAggAskValue: NaN,
        bestAggMidValue: NaN,
        bestPassiveBidValue: NaN,
        bestPassiveAskValue: NaN,
        bestPassiveMidValue: NaN,
        refBid: NaN,
        refAsk: NaN,
        voidBid: NaN,
        voidAsk: NaN,
        volatility: '',
        tradedVolume: '',
        totalExposure: '',
        realizedExposure: '',
        totalPredFunding: '',
      }

      if (componentMap.Hedger?.data?.states != null) {
        for (const key in componentMap.Hedger.data.states) {
          const value = Number(componentMap.Hedger.data.states[key])

          if (key.startsWith('bestAggBid')) {
            if (key.includes('|')) {
              const keySplits = key.substring(key.indexOf('.') + 1).split('|')
              const replacedKeys = keySplits.map(individualKey => replaceExchangeShortname(individualKey))
              mainSummaryData.bestAggBid = replacedKeys.join('|')
            } else {
              mainSummaryData.bestAggBid = replaceExchangeShortname(key.split('.')[1])
            }
            mainSummaryData.bestAggBidValue = value
          } else if (key.startsWith('bestPassiveBid')) {
            if (key.includes('|')) {
              const keySplits = key.substring(key.indexOf('.') + 1).split('|')
              const replacedKeys = keySplits.map(individualKey => replaceExchangeShortname(individualKey))
              mainSummaryData.bestPassiveBid = replacedKeys.join('|')
            } else {
              mainSummaryData.bestPassiveBid = replaceExchangeShortname(key.split('.')[1])
            }
            mainSummaryData.bestPassiveBidValue = value
          } else if (key.startsWith('bestAggAsk')) {
            if (key.includes('|')) {
              const keySplits = key.substring(key.indexOf('.') + 1).split('|')
              const replacedKeys = keySplits.map(individualKey => replaceExchangeShortname(individualKey))
              mainSummaryData.bestAggAsk = replacedKeys.join('|')
            } else {
              mainSummaryData.bestAggAsk = replaceExchangeShortname(key.split('.')[1])
            }
            mainSummaryData.bestAggAskValue = value
          } else if (key.startsWith('bestPassiveAsk')) {
            if (key.includes('|')) {
              const keySplits = key.substring(key.indexOf('.') + 1).split('|')
              const replacedKeys = keySplits.map(individualKey => replaceExchangeShortname(individualKey))
              mainSummaryData.bestPassiveAsk = replacedKeys.join('|')
            } else {
              mainSummaryData.bestPassiveAsk = replaceExchangeShortname(key.split('.')[1])
            }
            mainSummaryData.bestPassiveAskValue = value
          } else if (key.startsWith('bestAggMid')) {
            mainSummaryData.bestAggMidValue = value
          } else if (key.startsWith('bestPassiveMid')) {
            mainSummaryData.bestPassiveMidValue = value
          }
        }
      }

      mainSummaryData.refBid = componentMap.Quoter?.data?.states.Refbid ?? '[no data]'
      mainSummaryData.refAsk = componentMap.Quoter?.data?.states.Refask ?? '[no data]'
      mainSummaryData.voidBid = componentMap.Quoter?.data?.states.Voidbid ?? '[no data]'
      mainSummaryData.voidAsk = componentMap.Quoter?.data?.states.Voidask ?? '[no data]'
      mainSummaryData.volatility = componentMap.Quoter?.data?.states.Volatility ?? '[no data]'
      mainSummaryData.tradedVolume = componentMap.Risk?.data?.states['Traded Volume'] ?? '[no data]'
      mainSummaryData.totalExposure = componentMap.Risk?.data?.states['Total Exposure'] ?? '[no data]'
      mainSummaryData.realizedExposure = componentMap.Risk?.data?.states['Realized Exposure'] ?? '[no data]'
      mainSummaryData.totalPredFunding = componentMap.Risk?.data?.states.totalPredFunding ?? '[no data]'

      setMainSummaryData(mainSummaryData)
    }
  }, [mainSummaryDataGridWorker, componentMap])

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (window.Worker) {
      mainSummaryDataGridWorker.onmessage = (e: MessageEvent<StrategyDataGridRow[]>) => {
        setDataGridRows(e.data)
      }
    }
  }, [mainSummaryDataGridWorker])

  return { dataGridRows, mainSummaryData }
}
